import { Box, Flex, useMantineTheme } from '@mantine/core';
import { Banner } from '@repo/foundations/components/Banner/Banner';
import { Text } from '@repo/foundations/components/Text/Text';
import { memo, useCallback, useMemo } from 'react';
import { BaseOverviewChart } from '../../../components/Overview/BaseOverviewChart';
import type { StackedLineChartChannel } from '../../../utils/charts/StackedLineChart';
import { DATE_FILTER_OPTIONS, TIME_SAVINGS_CATEGORIES } from '../constants';
import type { TimeSavingsTimeseries } from '../types';
import { formatHoursToHm } from '../utils';
import { ChartSkeleton } from './ChartSkeleton';

interface AiTimeSavingsOverviewChartProps {
	timeseries?: TimeSavingsTimeseries;
	dateFilter: string;
	setDateFilter: (dateFilter: string) => void;
	setTimeRangeValue: (timeRangeValue: {
		timeRange: string;
		dateRange: { from: Date | null; to: Date | null };
	}) => void;
	timeRangeValue: {
		timeRange: string;
		dateRange: { from: Date | null; to: Date | null };
	};
	dateRangeOption?: 'day' | 'week' | 'month' | 'year';
	isLoading?: boolean;
	onClick?: (dataIndex: number) => void;
}

export const AiTimeSavingsOverviewChart = memo(
	({
		timeseries,
		dateFilter,
		setDateFilter,
		setTimeRangeValue,
		timeRangeValue,
		dateRangeOption = 'day',
		isLoading,
		onClick,
	}: AiTimeSavingsOverviewChartProps) => {
		const theme = useMantineTheme();

		const channels: StackedLineChartChannel<
			Record<string, unknown> & { date: Date },
			Date,
			number
		>[] = useMemo(
			() =>
				TIME_SAVINGS_CATEGORIES.map((category, index) => ({
					label: category.label,
					color: theme.other.getColor(`icon/chart/${category.chartIndex}`),
					x: 'date',
					y: category.key,
					renderArea: index % 2 === 0, // Alternate area rendering
					areaOpacity: 0.4,
					zIndex: (index + 1) * 10,
				})),
			[theme.other]
		);

		const tooltipDataRenderer = useCallback(
			(dataIndex: number) => {
				const point = (timeseries?.points ?? [])[dataIndex];
				if (!point) return null;

				return (
					<Box p="sm" maw={300}>
						<Text mb="sm" size="sm" color="text/secondary/default">
							{new Date(point.timestamp).toLocaleDateString([], {
								month: 'long',
								day: 'numeric',
								year: 'numeric',
							})}
						</Text>
						<Flex direction="column" gap="xs" mt="sm" mb="sm">
							{TIME_SAVINGS_CATEGORIES.map((category) => {
								const value = point[
									category.key as keyof typeof point
								] as number;
								return (
									<Flex key={category.key} justify="space-between" gap="sm">
										<Flex gap="xs" align="center">
											<Box
												w={16}
												h={2}
												bg={theme.other.getColor(
													`icon/chart/${category.chartIndex}`
												)}
												style={{ borderRadius: theme.radius.md }}
												aria-hidden="true"
											/>
											<Text size="sm">{category.label}</Text>
										</Flex>
										<Text fw={500} size="sm">
											{formatHoursToHm(value)}
										</Text>
									</Flex>
								);
							})}
						</Flex>
						<Banner
							inCard
							tone="info"
							message="Time savings tracking started on November 4th, 2025."
						/>
					</Box>
				);
			},
			[timeseries?.points, theme]
		);

		const yAxisFormatter = (value: number) => `${Math.round(value)}h`;

		const reducedTimeseries = useMemo(
			() => ({
				points: (timeseries?.points ?? []).map((p) => ({
					timestamp: p.timestamp,
					data_discovery_and_search: p.data_discovery_and_search,
					documentation_and_metadata_management:
						p.documentation_and_metadata_management,
					data_governance_and_compliance: p.data_governance_and_compliance,
					data_quality_and_monitoring: p.data_quality_and_monitoring,
					process_automation_and_workflows: p.process_automation_and_workflows,
				})),
				granularity: timeseries?.granularity ?? '',
				available_channels: timeseries?.available_channels ?? [],
				available_dimensions: timeseries?.available_dimensions ?? [],
			}),
			[timeseries]
		);

		const isEmpty = useMemo(() => {
			if (!timeseries || !timeseries.points || timeseries.points.length === 0) {
				return true;
			}

			return timeseries.points.every((point) =>
				TIME_SAVINGS_CATEGORIES.every(
					(category) =>
						(point[category.key as keyof typeof point] as number) === 0
				)
			);
		}, [timeseries]);

		const emptyStateMessage = useMemo(
			() => ({
				title: 'No time savings data exists yet',
				description:
					'Data is being collected for future reporting. Please check back later to see your time-saving metrics.',
			}),
			[]
		);

		if (isLoading && !timeseries) {
			return <ChartSkeleton />;
		}

		return (
			<BaseOverviewChart
				timeseries={reducedTimeseries}
				dateFilter={dateFilter}
				setDateFilter={setDateFilter}
				setTimeRangeValue={setTimeRangeValue}
				timeRangeValue={timeRangeValue}
				dateRangeOption={dateRangeOption}
				setDateRangeOption={() => {}}
				title="Time savings"
				dateFilterOptions={DATE_FILTER_OPTIONS}
				channels={channels}
				tooltipDataRenderer={tooltipDataRenderer}
				analyticsEventPrefix="ai_time_savings/overview"
				yAxisFormatter={yAxisFormatter}
				hideDateFilter={true}
				hideDateRangeButtons={true}
				onClick={onClick}
				showEmptyStateMessage={isEmpty}
				emptyStateMessage={isEmpty ? emptyStateMessage : undefined}
			/>
		);
	}
);
